import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Minus from "@insite/mobius/Icons/Minus";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import MenuDownArrow from "@tIntermatic/Icons/MenuDownArrow";
// eslint-disable-next-line spire/fenced-imports
import Plus from "@tIntermatic/Icons/Plus";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    view: state.pages.productList.view,
    productsDataView: getProductListDataView(state),
    searchQuery: state.pages.productList.parameter?.search,
    searchWithinQuery: state.pages.productList?.filteredApiParameter?.searchWithin,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    containersinglegridItem?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    containersinglegridItem: {
        gap: 20,
        css: css`
            div[class^="GridOffset"] {
                display: flex;
                justify-content: center;
            }
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
    accordionSection: {
        toggleIconProps: {
            src: ChevronDown,
            size: 20,
            css: css`
                transform: rotateX(180deg);
            `,
        },

        expandIconProps: {
            src: ChevronDown,
            size: 20,
            css: css`
                transform: rotateX(10deg);
            `,
        },

        headerProps: {
            css: css`
                > button {
                    background: #fff;
                    border: none;
                    padding: 13px 9px 13px 15px !important;
                    > span {
                        font-size: 15px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 20px;
                        color: #333333;
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                padding-top: 0px;
                font-size: 16px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 24px;
                color: #33333a;
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({ id, view, productsDataView, searchQuery, searchWithinQuery }) => {
    const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle =
        view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;

    const displayView = () => {
        return (
            <GridItem {...leftColumnGridItemStyle}>
                <Hidden
                    css={css`
                        width: 100%;
                        padding-right: 8px;
                        @media all and (max-width: 991px) and (min-width: 0px) {
                            display: none;
                        }
                    `}
                >
                    <Zone contentId={id} zoneName="Content00" />
                </Hidden>
                <Hidden
                    css={css`
                        width: 100%;
                        @media (min-width: 992px) {
                            display: none;
                        }
                    `}
                >
                    <Accordion
                        headingLevel={1}
                        css={css`
                            border: 1px solid rgba(0, 0, 0, 0.1);
                        `}
                    >
                        <ManagedAccordionSection title="FILTERS" {...styles.accordionSection}>
                            <Zone contentId={id} zoneName="Content1" />
                        </ManagedAccordionSection>
                    </Accordion>
                </Hidden>
            </GridItem>
        );
    };

    const conditionalView = () => {
        if (searchWithinQuery) {
            return displayView();
        }
        if (searchQuery && productsDataView.value?.length === 0) {
            return null;
        }
        return displayView();
    };
    return (
        <GridContainer
            {...(searchQuery && productsDataView.value?.length === 0 && !searchWithinQuery
                ? styles.containersinglegridItem
                : styles.container)}
        >
            {conditionalView()}
            <GridItem {...rightColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
            {searchQuery && productsDataView.value?.length === 0 && !searchWithinQuery && (
                <StyledWrapper
                    css={css`
                        padding-bottom: 200px;
                        width: 100%;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        padding-bottom: 50px;
                                        width: 100%;
                                    `,
                                    css`
                                        padding-bottom: 50px;
                                        width: 100%;
                                    `,
                                    css`
                                        padding-bottom: 200px;
                                        width: 100%;
                                    `,
                                    css`
                                        padding-bottom: 200px;
                                        width: 100%;
                                    `,
                                    css`
                                        padding-bottom: 200px;
                                        width: 100%;
                                    `,
                                ],
                                "min",
                            )}
                    `}
                >
                    <Zone contentId={id} zoneName="Nosearch" />
                </StyledWrapper>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
